
import React, { useEffect, useRef } from 'react';

interface TurnstileProps {
  onVerify: (token: string) => void;
  onExpire?: () => void;
  onWidgetId?: (widgetId: string) => void;
  siteKey?: string;
}

export const Turnstile = ({ 
  onVerify, 
  onExpire,
  onWidgetId,
  siteKey = "0x4AAAAAAA_5RO-6Yvr336Jr" // Updated with the actual Cloudflare Turnstile site key
}: TurnstileProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetId = useRef<string | null>(null);

  useEffect(() => {
    // Load the Turnstile script if it's not already loaded
    if (!window.turnstile) {
      const script = document.createElement('script');
      script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);

      script.onload = renderTurnstile;
    } else {
      renderTurnstile();
    }

    return () => {
      // Reset the turnstile when component unmounts
      if (widgetId.current !== null && window.turnstile) {
        try {
          window.turnstile.remove(widgetId.current);
        } catch (e) {
          console.warn("Could not remove turnstile widget:", e);
        }
      }
    };
  }, [siteKey]);

  const renderTurnstile = () => {
    if (!containerRef.current || !window.turnstile) {
      // If not ready yet, wait and try again
      setTimeout(renderTurnstile, 100);
      return;
    }

    try {
      // Clear any existing widget
      if (widgetId.current !== null) {
        try {
          window.turnstile.remove(widgetId.current);
        } catch (e) {
          console.warn("Could not reset existing turnstile:", e);
        }
      }
      
      // Create a new turnstile widget and save its ID
      widgetId.current = window.turnstile.render(containerRef.current, {
        sitekey: siteKey,
        callback: (token: string) => {
          onVerify(token);
        },
        'expired-callback': onExpire,
        'theme': 'light',
      });
      
      // Pass the widget ID back to the parent component if needed
      if (onWidgetId && widgetId.current) {
        onWidgetId(widgetId.current);
      }
    } catch (error) {
      console.error("Error rendering Turnstile:", error);
    }
  };

  return <div ref={containerRef} className="mt-4 mb-6"></div>;
};
